import React from 'react';
import { AnimatePresence, motion } from 'motion/react';
import BrandLogo from '../BrandLogo';
import AuthAlert from './AuthAlert';
import AuthModePill from './AuthModePill';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import { cardVariants, reducedVariants } from './authMotion';
import { PresenceSwap } from '../../motion';
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';

export default function AuthCard({
  mode,
  onModeChange,
  loading,
  error,
  success,
  onLogin,
  onSignup,
  prefilledUsername = '',
  showAuthRequired = false,
}) {
  const reducedMotion = usePrefersReducedMotion();
  const isSignup = mode === 'signup';
  const variants = reducedVariants(cardVariants, reducedMotion);

  return (
    <motion.div
      variants={variants}
      initial="hidden"
      animate="visible"
      className="w-full rounded-2xl border border-gray-100 bg-white p-6 shadow-xl shadow-gray-900/5 sm:p-8 md:p-10"
    >
      <div className="mb-5 flex justify-center sm:mb-6 lg:hidden">
        <BrandLogo asLink={false} markClassName="h-10 w-10" wordmarkClassName="h-5" />
      </div>

      <AuthModePill
        mode={mode}
        onChange={onModeChange}
        disabled={loading}
      />

      <div className="mb-5 text-center sm:mb-6">
        <h1 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
          {isSignup ? 'Create your account' : 'Welcome back'}
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          {isSignup
            ? 'Register with your organization invite key to access Vitra.'
            : 'Sign in to continue to your dashboard.'}
        </p>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        {showAuthRequired && (
          <AuthAlert
            key="auth-required"
            type="error"
            message="Please sign in to continue."
          />
        )}
        {error && <AuthAlert key="error" type="error" message={error} />}
        {success && <AuthAlert key="success" type="success" message={success} />}
      </AnimatePresence>

      <PresenceSwap swapKey={mode} order={['login', 'signup']}>
        {isSignup ? (
          <SignupForm onSubmit={onSignup} loading={loading} />
        ) : (
          <LoginForm
            key={prefilledUsername || 'login'}
            onSubmit={onLogin}
            loading={loading}
            initialUsername={prefilledUsername}
          />
        )}
      </PresenceSwap>
    </motion.div>
  );
}
